import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { format, startOfMonth, endOfMonth, differenceInCalendarDays } from 'date-fns';
import {
  getProjectionScenarios, getProjectionScenario,
  saveProjectionScenario, deleteProjectionScenario,
} from '../../lib/supabase';

import { billRatesFromCatalog, SKILLED_BILL_FALLBACK, UNSKILLED_BILL_FALLBACK, WEEKS_PER_MONTH } from '../../lib/authorizedRevenue';
import { isClosedClient } from '../../lib/serviceModel';

// Bill rates come from service_catalog through lib/authorizedRevenue.js (the
// Overview's authorized-vs-delivered strip uses the same), overhead from the
// last three months entered on the Overhead tab, scaled to the period.
const SKILLED_BILL_DEFAULT   = SKILLED_BILL_FALLBACK;
const UNSKILLED_BILL_DEFAULT = UNSKILLED_BILL_FALLBACK;
const OVERHEAD_MONTHLY_DEFAULT = 5000;
const SKILLED_TITLES = ['RN', 'LPN'];

// Mirrors the pipeline zones on src/pages/Pipeline.js (clients.client_status_zone)
// so "batch load" pulls the same natural groups the team already works from.
const PIPELINE_ZONES = [
  { key: 'Outreach',        label: 'Outreach',           color: 'var(--zone-marketing)',  bg: 'var(--zone-marketing-bg)' },
  { key: 'Clinical Intake', label: 'Clinical Intake',    color: 'var(--zone-processing)', bg: 'var(--zone-processing-bg)' },
  { key: 'Authorization',   label: 'Authorization',      color: 'var(--zone-approval)',   bg: 'var(--zone-approval-bg)' },
  { key: 'Active',          label: 'Active / In Service', color: 'var(--zone-active)',    bg: 'var(--zone-active-bg)' },
  { key: 'Inactive',        label: 'Inactive',           color: 'var(--zone-closed)',     bg: 'var(--zone-closed-bg)' },
];

const fmt$  = (n) => '$' + Math.round(n || 0).toLocaleString('en-US');
const fmtH  = (n) => (n || 0).toLocaleString('en-US', { maximumFractionDigits: 1 }) + 'h';
const r2    = (n) => Math.round((n || 0) * 100) / 100;
const defaultRange = () => ({ start: format(startOfMonth(new Date()), 'yyyy-MM-dd'), end: format(endOfMonth(new Date()), 'yyyy-MM-dd') });

export default function Projections() {
  const { profile } = useAuth();
  const uidRef = useRef(1);
  const nextUid = () => `r${uidRef.current++}`;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const [clients, setClients] = useState([]);       // all clients (roster)
  const [caregivers, setCaregivers] = useState([]);  // active staff with a field-delivery role (RN/LPN/PCA)
  const [avgPay, setAvgPay] = useState({ Skilled: 50, Unskilled: 19 });

  const [rows, setRows] = useState([]);
  // `overhead` is a MONTHLY figure; the total for the period is derived from
  // it below, so hours and overhead scale together instead of one growing
  // with the range while the other sat at a flat number.
  const [assumptions, setAssumptions] = useState({
    skilledBill: SKILLED_BILL_DEFAULT,
    unskilledBill: UNSKILLED_BILL_DEFAULT,
    overhead: OVERHEAD_MONTHLY_DEFAULT,
  });
  const [overheadSource, setOverheadSource] = useState('default');   // 'default' | 'entered' | 'scenario'
  const [dateRange, setDateRange] = useState(defaultRange());

  const [scenarios, setScenarios] = useState([]);
  const [scenarioId, setScenarioId] = useState('');
  const [scenarioName, setScenarioName] = useState('');
  const [dirty, setDirty] = useState(false);
  const [savingAs, setSavingAs] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [savedNote, setSavedNote] = useState('');

  const [clientQuery, setClientQuery] = useState('');
  const [clientMenuOpen, setClientMenuOpen] = useState(false);

  const markDirty = () => setDirty(true);

  const weeksInRange = useMemo(() => {
    const days = differenceInCalendarDays(new Date(dateRange.end), new Date(dateRange.start)) + 1;
    return Math.max(days, 1) / 7;
  }, [dateRange]);
  const overheadForPeriod = useMemo(() => assumptions.overhead * (weeksInRange / WEEKS_PER_MONTH), [assumptions.overhead, weeksInRange]);

  // ── initial load ──────────────────────────────────────────────────────
  useEffect(() => { loadBase(true); loadScenarioList(); }, []); // eslint-disable-line

  async function loadScenarioList() {
    const { data } = await getProjectionScenarios();
    setScenarios(data || []);
  }

  async function loadBase(seedIfEmpty) {
    setError(null);

    const [clientsRes, staffRes, catalogRes, overheadRes] = await Promise.all([
      supabase.from('clients')
        .select('id, client_id, first_name, last_name, current_status, client_status_zone, pa_skilled_hours_week, pa_pss_hours_week')
        .order('last_name'),
      supabase.from('staff')
        .select('id, full_name, first_name, last_name, position_code, is_lead, pay_rate, pay_type, status')
        .in('position_code', ['RN', 'LPN', 'PCA', 'BHA']).eq('status', 'active')
        .order('last_name'),
      supabase.from('service_catalog').select('code, skill_kind, default_charge_rate, billable, active').eq('active', true).eq('billable', true),
      supabase.from('overhead_expenses').select('month, amount').order('month', { ascending: false }),
    ]);

    if (clientsRes.error || staffRes.error) {
      setError(clientsRes.error?.message || staffRes.error?.message || 'Failed to load data');
      setLoading(false);
      return null;
    }

    const clientsData = clientsRes.data || [];
    // Averages are per hour, so only hourly staff count toward them; a weekly
    // salary averaged in as if it were an hourly rate used to skew both.
    const staffData = (staffRes.data || []).filter(s => s.pay_rate != null && (s.pay_type || 'hourly') === 'hourly');

    const { skilledBill, unskilledBill } = billRatesFromCatalog(catalogRes.data || []);

    // Overhead: the average of the last three months that have entries.
    const byMonth = {};
    for (const o of overheadRes.data || []) byMonth[o.month.slice(0, 7)] = (byMonth[o.month.slice(0, 7)] || 0) + Number(o.amount || 0);
    const recent = Object.keys(byMonth).sort().reverse().slice(0, 3).map(k => byMonth[k]);
    const overheadMonthly = recent.length ? r2(recent.reduce((s, n) => s + n, 0) / recent.length) : null;

    const avg = (arr) => arr.length ? arr.reduce((s, x) => s + Number(x.pay_rate), 0) / arr.length : null;
    const overallAvg = avg(staffData);
    const skilledTitled = staffData.filter(s => SKILLED_TITLES.includes(s.position_code));
    const unskilledTitled = staffData.filter(s => !SKILLED_TITLES.includes(s.position_code));
    const avgPayCalc = {
      Skilled: r2(avg(skilledTitled) || overallAvg || 50),
      Unskilled: r2(avg(unskilledTitled) || overallAvg || 19),
    };

    setClients(clientsData);
    setCaregivers(staffData);
    setAvgPay(avgPayCalc);
    if (seedIfEmpty) {
      // A fresh sheet starts from live inputs. A loaded scenario keeps its own.
      setAssumptions({ skilledBill, unskilledBill, overhead: overheadMonthly ?? OVERHEAD_MONTHLY_DEFAULT });
      setOverheadSource(overheadMonthly != null ? 'entered' : 'default');
    }
    setLoading(false);

    const base = { clientsData, staffData, avgPayCalc, skilledBill, unskilledBill };
    if (seedIfEmpty) {
      // Start from the clients actually in service. Prospects in Outreach
      // and Clinical Intake are one batch-load click away, not the default —
      // seeding with all of them projected income for referrals never taken.
      const active = clientsData.filter(c => c.client_status_zone === 'Active');
      const pool = active.length ? active : clientsData.filter(c => !isClosedClient(c));
      const seeded = pool.flatMap(c => rowsForClient(c, avgPayCalc, { skilledBill, unskilledBill }));
      setRows(seeded);
    }
    return base;
  }

  // Always emits both a Skilled and an Unskilled line for the client — 0 hrs/wk
  // where there's no Prior Auth hours for that service, left for manual entry.
  function rowsForClient(c, avgPayCalc, bills) {
    const name = `${c.first_name || ''} ${c.last_name || ''}`.trim();
    const ap = avgPayCalc || avgPay;
    const b = bills || assumptions;
    const build = (svc, weeklyHrs) => ({
      uid: nextUid(),
      clientDbId: c.id, clientCode: c.client_id, clientName: name,
      svc, caregiverId: '',
      weeklyHrs: r2(weeklyHrs || 0),
      pay: ap[svc],
      bill: svc === 'Skilled' ? b.skilledBill : b.unskilledBill, billOverride: false,
    });
    return [
      build('Skilled', Number(c.pa_skilled_hours_week) || 0),
      build('Unskilled', Number(c.pa_pss_hours_week) || 0),
    ];
  }

  // Refreshes Hrs/Week and Pay Rate for every real (non-hypothetical) row from
  // live data, and backfills a missing Skilled or Unskilled sibling line for
  // any client that doesn't have both yet (e.g. a scenario saved before both
  // lines were always created).
  function applyRefreshedRows(base) {
    setRows(rs => {
      const next = rs.map(r => {
        if (!r.clientDbId) return r; // leave hypothetical rows untouched
        const c = base.clientsData.find(x => x.id === r.clientDbId);
        if (!c) return r;
        const cg = r.caregiverId ? base.staffData.find(s => s.id === r.caregiverId) : null;
        const pay = r.caregiverId ? (cg ? r2(Number(cg.pay_rate)) : r.pay) : base.avgPayCalc[r.svc];
        const weeklyHrs = r2(r.svc === 'Skilled' ? Number(c.pa_skilled_hours_week) || 0 : Number(c.pa_pss_hours_week) || 0);
        return { ...r, pay, weeklyHrs };
      });

      const have = new Set(next.filter(r => r.clientDbId).map(r => r.clientDbId + '|' + r.svc));
      const sampleByClient = new Map();
      next.forEach(r => { if (r.clientDbId && !sampleByClient.has(r.clientDbId)) sampleByClient.set(r.clientDbId, r); });

      const additions = [];
      sampleByClient.forEach((sample, clientDbId) => {
        const c = base.clientsData.find(x => x.id === clientDbId);
        if (!c) return;
        ['Skilled', 'Unskilled'].forEach(svc => {
          if (have.has(clientDbId + '|' + svc)) return;
          const weeklyHrs = svc === 'Skilled' ? Number(c.pa_skilled_hours_week) || 0 : Number(c.pa_pss_hours_week) || 0;
          additions.push({
            uid: nextUid(), clientDbId: c.id, clientCode: c.client_id, clientName: sample.clientName,
            svc, caregiverId: '', weeklyHrs: r2(weeklyHrs),
            pay: base.avgPayCalc[svc],
            bill: svc === 'Skilled' ? assumptions.skilledBill : assumptions.unskilledBill, billOverride: false,
          });
        });
      });

      return [...next, ...additions];
    });
  }

  // ── derived ───────────────────────────────────────────────────────────
  // A hypothetical client is identified by the id it was created with, so two
  // "New Client" rows stay two clients and renaming one does not rename the
  // other. Scenarios saved before groupId existed fall back to the name.
  const groupKeyOf = (r) => r.clientDbId || (r.groupId ? `new:${r.groupId}` : `new|${r.clientName}`);
  const groups = useMemo(() => {
    const order = [];
    const map = new Map();
    rows.forEach(r => {
      const key = groupKeyOf(r);
      if (!map.has(key)) { map.set(key, []); order.push(key); }
      map.get(key).push(r);
    });
    return order.map(k => ({ key: k, rows: map.get(k) }));
  }, [rows]);

  const computed = useMemo(() => rows.map(r => {
    const hrsMo = r2(r.weeklyHrs * weeksInRange);
    const income = r.bill * hrsMo;
    const cost = r.pay * hrsMo;
    return { ...r, hrsMo, income, cost, profit: income - cost };
  }), [rows, weeksInRange]);

  const totals = useMemo(() => {
    const t = computed.reduce((acc, r) => {
      acc.hrsMo += r.hrsMo;
      acc.income += r.income;
      acc.cost += r.cost;
      acc.profit += r.profit;
      return acc;
    }, { hrsMo: 0, income: 0, cost: 0, profit: 0 });
    t.netProfit = t.profit - overheadForPeriod;
    return t;
  }, [computed, overheadForPeriod]);

  const addedClientIds = useMemo(() => new Set(rows.map(r => r.clientDbId).filter(Boolean)), [rows]);
  const rosterMatches = useMemo(() => {
    const q = clientQuery.trim().toLowerCase();
    return clients
      .filter(c => !addedClientIds.has(c.id))
      .filter(c => !q || `${c.first_name} ${c.last_name}`.toLowerCase().includes(q) || (c.client_id || '').toLowerCase().includes(q))
      .slice(0, 30);
  }, [clients, addedClientIds, clientQuery]);

  const zoneCounts = useMemo(() => {
    const counts = {};
    PIPELINE_ZONES.forEach(z => {
      counts[z.key] = clients.filter(c => c.client_status_zone === z.key && !addedClientIds.has(c.id)).length;
    });
    return counts;
  }, [clients, addedClientIds]);

  // ── mutators ──────────────────────────────────────────────────────────
  const setCellNumber = (uid, field) => (e) => {
    const v = parseFloat(e.target.value);
    setRows(rs => rs.map(r => r.uid === uid ? { ...r, [field]: isNaN(v) ? 0 : v } : r));
    markDirty();
  };

  const setBill = (uid) => (e) => {
    const v = parseFloat(e.target.value);
    setRows(rs => rs.map(r => {
      if (r.uid !== uid) return r;
      const bill = isNaN(v) ? 0 : v;
      const defaultBill = r.svc === 'Skilled' ? assumptions.skilledBill : assumptions.unskilledBill;
      return { ...r, bill, billOverride: bill !== defaultBill };
    }));
    markDirty();
  };

  const resetBill = (uid) => {
    setRows(rs => rs.map(r => {
      if (r.uid !== uid) return r;
      const bill = r.svc === 'Skilled' ? assumptions.skilledBill : assumptions.unskilledBill;
      return { ...r, bill, billOverride: false };
    }));
    markDirty();
  };

  const setCaregiver = (uid) => (e) => {
    const val = e.target.value;
    setRows(rs => rs.map(r => {
      if (r.uid !== uid) return r;
      if (!val) return { ...r, caregiverId: '', pay: avgPay[r.svc] };
      const cg = caregivers.find(c => c.id === val);
      return { ...r, caregiverId: val, pay: cg ? r2(Number(cg.pay_rate)) : r.pay };
    }));
    markDirty();
  };

  // Renames every row in the client's group at once (a hypothetical client is
  // always both a Skilled and an Unskilled row sharing one name).
  const renameHypothetical = (groupKey) => (e) => {
    const val = e.target.value;
    setRows(rs => rs.map(r => groupKeyOf(r) === groupKey ? { ...r, clientName: val } : r));
    markDirty();
  };

  const removeRow = (uid) => { setRows(rs => rs.filter(r => r.uid !== uid)); markDirty(); };
  const removeClientGroup = (key) => {
    setRows(rs => rs.filter(r => groupKeyOf(r) !== key));
    markDirty();
  };

  const updateAssumption = (field) => (e) => {
    const v = parseFloat(e.target.value);
    const val = isNaN(v) ? 0 : v;
    setAssumptions(a => {
      const next = { ...a, [field]: val };
      if (field === 'skilledBill' || field === 'unskilledBill') {
        const svc = field === 'skilledBill' ? 'Skilled' : 'Unskilled';
        setRows(rs => rs.map(r => (r.svc === svc && !r.billOverride) ? { ...r, bill: val } : r));
      }
      return next;
    });
    markDirty();
  };

  const addHypotheticalRow = () => {
    const groupId = nextUid();
    const build = (svc) => ({
      uid: nextUid(), groupId, clientDbId: null, clientCode: '—', clientName: 'New Client',
      svc, caregiverId: '', weeklyHrs: 0,
      pay: avgPay[svc],
      bill: svc === 'Skilled' ? assumptions.skilledBill : assumptions.unskilledBill, billOverride: false,
    });
    setRows(rs => [...rs, build('Skilled'), build('Unskilled')]);
    markDirty();
  };

  const addClientFromRoster = (c) => {
    const newRows = rowsForClient(c, avgPay);
    setRows(rs => [...rs, ...newRows]);
    setClientQuery(''); setClientMenuOpen(false);
    markDirty();
  };

  // Batch-add every client in a pipeline zone (see PIPELINE_ZONES) that isn't
  // already a row — re-derives the "already added" set from current rows each
  // click, so re-clicking a zone (or clicking after manual adds) never duplicates.
  const addZoneClients = (zoneKey) => {
    const toAdd = clients.filter(c => c.client_status_zone === zoneKey && !addedClientIds.has(c.id));
    if (!toAdd.length) return;
    const newRows = toAdd.flatMap(c => rowsForClient(c, avgPay));
    setRows(rs => [...rs, ...newRows]);
    markDirty();
  };

  async function handleRefresh() {
    setRefreshing(true);
    const base = await loadBase(false);
    if (base) applyRefreshedRows(base);
    setRefreshing(false);
  }

  const handleDateChange = (field) => (e) => {
    const val = e.target.value;
    const nextRange = { ...dateRange, [field]: val };
    if (!val || new Date(nextRange.start) > new Date(nextRange.end)) return;
    setDateRange(nextRange);
    markDirty();
  };

  // ── scenarios ─────────────────────────────────────────────────────────
  async function handleLoadScenario(id) {
    if (!id) {
      setScenarioId(''); setScenarioName(''); setDirty(false); setDateRange(defaultRange());
      loadBase(true);
      return;
    }
    const { data, error: err } = await getProjectionScenario(id);
    if (err || !data) { setError(err?.message || 'Could not load scenario'); return; }
    setScenarioId(data.id);
    setScenarioName(data.name);
    setRows((data.payload?.rows || []).map(r => ({ ...r, uid: nextUid() })));
    setAssumptions(a => ({ ...a, ...(data.payload?.assumptions || {}) }));
    if (data.payload?.assumptions?.overhead != null) setOverheadSource('scenario');
    setDateRange(data.payload?.dateRange || defaultRange());
    setDirty(false);
  }

  async function handleSave() {
    if (!scenarioId) { setDraftName(scenarioName || 'Current State — ' + format(new Date(), 'MMM yyyy')); setSavingAs(true); return; }
    const payload = { rows: rows.map(({ uid, ...r }) => r), assumptions, dateRange };
    const { data, error: err } = await saveProjectionScenario({ id: scenarioId, name: scenarioName, payload, profile });
    if (err) { setError(err.message); return; }
    setDirty(false);
    setSavedNote('Saved'); setTimeout(() => setSavedNote(''), 1800);
    loadScenarioList();
    if (data) setScenarioName(data.name);
  }

  async function handleConfirmSaveAs() {
    const name = draftName.trim() || 'Untitled scenario';
    const payload = { rows: rows.map(({ uid, ...r }) => r), assumptions, dateRange };
    const { data, error: err } = await saveProjectionScenario({ id: null, name, payload, profile });
    if (err) { setError(err.message); return; }
    setScenarioId(data.id); setScenarioName(data.name);
    setSavingAs(false); setDirty(false);
    setSavedNote('Saved'); setTimeout(() => setSavedNote(''), 1800);
    loadScenarioList();
  }

  async function handleDeleteScenario() {
    if (!scenarioId) return;
    if (!window.confirm(`Delete scenario "${scenarioName}"? This cannot be undone.`)) return;
    const { error: err } = await deleteProjectionScenario(scenarioId);
    if (err) { setError(err.message); return; }
    setScenarioId(''); setScenarioName(''); setDirty(false);
    loadScenarioList();
    setDateRange(defaultRange());
    loadBase(true);
  }

  // ── render ────────────────────────────────────────────────────────────
  if (loading) return <div className="empty-state"><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>;

  const inputSm = { width: 56, textAlign: 'right', background: 'var(--accent-bg)', border: '0.5px solid var(--accent-border)', borderRadius: 5, padding: '3px 6px', fontSize: 12, color: 'var(--text)', fontFamily: 'inherit' };
  const selectSm = { background: 'var(--accent-bg)', border: '0.5px solid var(--accent-border)', borderRadius: 5, padding: '3px 6px', fontSize: 11.5, color: 'var(--text)', fontFamily: 'inherit', maxWidth: 160 };
  const dateSm = { background: 'var(--card-bg)', border: '0.5px solid var(--border2)', borderRadius: 5, padding: '4px 7px', fontSize: 11.5, color: 'var(--text)', fontFamily: 'inherit', width: 118 };
  const tag = (svc) => (
    <span style={{
      display: 'inline-block', padding: '1px 7px', borderRadius: 20, fontSize: 10, fontWeight: 700,
      background: svc === 'Skilled' ? 'var(--accent-bg)' : 'var(--copper-bg)',
      color: svc === 'Skilled' ? 'var(--accent)' : 'var(--copper)',
      border: `0.5px solid ${svc === 'Skilled' ? 'var(--accent-border)' : 'var(--copper-border)'}`,
    }}>{svc}</span>
  );
  const tbGroup = { display: 'flex', alignItems: 'center', gap: 7, padding: '8px 14px', borderRight: '0.5px solid var(--border)' };
  const tbLabel = { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text3)', whiteSpace: 'nowrap' };
  const th = { textAlign: 'right', fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text3)', padding: '6px 10px', borderBottom: '0.5px solid var(--border2)', borderRight: '0.5px solid var(--border)', whiteSpace: 'nowrap' };
  const td = { padding: '4px 10px', borderBottom: '0.5px solid var(--border)', borderRight: '0.5px solid var(--border)', verticalAlign: 'middle', textAlign: 'right', whiteSpace: 'nowrap' };

  return (
    <div>
      <p style={{ margin: '0 0 10px', fontSize: 12.5, color: 'var(--text3)', maxWidth: '80ch' }}>
        Scratch-pad model of projected income, caregiver cost and margin per client, pre-filled from prior auth and fully editable.
        The authorized-vs-delivered figure on the Finance overview is the same arithmetic without the sliders.
      </p>

      {error && (
        <div style={{ padding: '10px 14px', borderRadius: 8, background: 'var(--danger-bg)', color: 'var(--danger)', fontSize: 12.5, marginBottom: 10 }}>{error}</div>
      )}

      {/* dense toolbar — scenario, period, assumptions, actions all in one row */}
      <div style={{ display: 'flex', flexWrap: 'wrap', border: '0.5px solid var(--border2)', borderRadius: '8px 8px 0 0', background: 'var(--card-bg)' }}>
        <div style={tbGroup}>
          <span style={tbLabel}>Scenario</span>
          <select style={{ ...selectSm, maxWidth: 190, padding: '5px 24px 5px 8px' }} value={scenarioId} onChange={e => handleLoadScenario(e.target.value)}>
            <option value="">— New / unsaved —</option>
            {scenarios.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <button className="btn btn-ghost btn-sm" title="Start a fresh unsaved scenario" onClick={() => handleLoadScenario('')}>+ New</button>
        </div>
        <div style={tbGroup}>
          <span style={tbLabel}>Period</span>
          <input type="date" style={dateSm} value={dateRange.start} max={dateRange.end} onChange={handleDateChange('start')} />
          <span style={{ color: 'var(--text3)' }}>–</span>
          <input type="date" style={dateSm} value={dateRange.end} min={dateRange.start} onChange={handleDateChange('end')} />
          <span style={{ fontSize: 10.5, color: 'var(--text3)' }}>≈ {weeksInRange.toFixed(1)} wks</span>
        </div>
        <div style={tbGroup}>
          <span style={tbLabel}>Skilled $/hr</span>
          <input type="number" step="0.01" style={inputSm} value={assumptions.skilledBill} onChange={updateAssumption('skilledBill')} />
          <span style={{ ...tbLabel, marginLeft: 6 }}>Unskilled $/hr</span>
          <input type="number" step="0.01" style={inputSm} value={assumptions.unskilledBill} onChange={updateAssumption('unskilledBill')} />
          <span style={{ ...tbLabel, marginLeft: 6 }} title={overheadSource === 'entered' ? 'Average of the last three months on Finance › P&L › Overhead' : overheadSource === 'scenario' ? 'Saved with this scenario' : 'No overhead entered yet — placeholder'}>Overhead $/mo{overheadSource === 'default' ? ' *' : ''}</span>
          <input type="number" step="100" style={{ ...inputSm, width: 70 }} value={assumptions.overhead} onChange={updateAssumption('overhead')} />
        </div>
        <div style={{ ...tbGroup, marginLeft: 'auto', borderRight: 'none', gap: 8 }}>
          {savedNote && <span style={{ fontSize: 11.5, color: 'var(--success)', fontWeight: 600 }}>{savedNote}</span>}
          {dirty && !savedNote && <span style={{ fontSize: 11, color: 'var(--copper)', fontWeight: 600 }}>● Unsaved changes</span>}
          {scenarioId && (
            <button className="btn btn-ghost btn-sm" title="Delete scenario" onClick={handleDeleteScenario} style={{ color: 'var(--danger)' }}>Delete</button>
          )}
          <button className="btn btn-outline btn-sm" onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? 'Refreshing…' : 'Refresh from live data'}
          </button>
          {savingAs ? (
            <span style={{ display: 'inline-flex', gap: 6 }}>
              <input className="form-input" style={{ width: 180, padding: '5px 9px', fontSize: 12 }} autoFocus
                value={draftName} onChange={e => setDraftName(e.target.value)} placeholder="Scenario name" />
              <button className="btn btn-primary btn-sm" onClick={handleConfirmSaveAs}>Save</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setSavingAs(false)}>Cancel</button>
            </span>
          ) : (
            <button className="btn btn-primary btn-sm" onClick={handleSave}>
              {scenarioId ? 'Save' : 'Save scenario'}
            </button>
          )}
        </div>
      </div>

      {/* metrics — single-row spreadsheet-total-style strip, not stat cards */}
      <div style={{ display: 'flex', flexWrap: 'wrap', border: '0.5px solid var(--border2)', borderTop: 'none', background: 'var(--bg2)' }}>
        {[
          { label: 'Approved Hrs / Period', value: fmtH(totals.hrsMo), sub: `${groups.length} clients · ${weeksInRange.toFixed(1)} wks` },
          { label: 'Projected Income', value: fmt$(totals.income), sub: 'if delivered in full' },
          { label: 'Projected Pay Cost', value: fmt$(totals.cost), sub: 'caregiver wages' },
          { label: 'Projected Net Profit', value: fmt$(totals.netProfit), sub: `after ${fmt$(overheadForPeriod)} overhead for the period`, danger: totals.netProfit < 0 },
        ].map((m, i) => (
          <div key={i} style={{ flex: '1 1 180px', padding: '9px 16px', borderRight: '0.5px solid var(--border)' }}>
            <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text3)', marginBottom: 3 }}>{m.label}</div>
            <div style={{ fontFamily: 'var(--font-display, inherit)', fontSize: 18, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: m.danger ? 'var(--danger)' : 'var(--text)' }}>{m.value}</div>
            <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{m.sub}</div>
          </div>
        ))}
      </div>

      {/* legend + batch load */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', padding: '7px 14px', border: '0.5px solid var(--border2)', borderTop: 'none', fontSize: 10.5, color: 'var(--text3)' }}>
        <span><i style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: 'var(--accent-bg)', border: '0.5px solid var(--accent-border)', marginRight: 5, verticalAlign: -1 }} />Editable</span>
        <span><i style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: 'var(--copper)', marginRight: 6, verticalAlign: -1 }} />Overrides the service-type bill rate</span>
        <span style={{ marginLeft: 'auto', fontWeight: 600 }}>Batch load from pipeline:</span>
        {PIPELINE_ZONES.map(z => {
          const count = zoneCounts[z.key] || 0;
          return (
            <button key={z.key} onClick={() => addZoneClients(z.key)} disabled={count === 0}
              title={count === 0 ? `No unadded clients in ${z.label}` : `Add all ${count} client${count === 1 ? '' : 's'} in ${z.label}`}
              style={{
                padding: '3px 10px', borderRadius: 20, fontSize: 10.5, fontWeight: 600, fontFamily: 'inherit',
                border: `0.5px solid ${count === 0 ? 'var(--border)' : z.color}`,
                background: count === 0 ? 'transparent' : z.bg,
                color: count === 0 ? 'var(--text3)' : z.color,
                cursor: count === 0 ? 'default' : 'pointer', opacity: count === 0 ? 0.5 : 1,
              }}>
              {z.label} · {count}
            </button>
          );
        })}
      </div>

      {/* table — real gridlines, dense */}
      <div style={{ overflowX: 'auto', border: '0.5px solid var(--border2)', borderTop: 'none' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 1180 }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left' }}>Client</th>
              <th style={{ ...th, textAlign: 'left' }}>Service</th>
              <th style={{ ...th, textAlign: 'left' }}>Assigned Caregiver</th>
              <th style={th}>Hrs / Week</th>
              <th style={th}>Pay Rate</th>
              <th style={th}>Bill Rate</th>
              <th style={th}>Hrs / Period</th>
              <th style={th}>Income / Period</th>
              <th style={th}>Pay Cost / Period</th>
              <th style={{ ...th, borderRight: 'none' }}>Profit / Period</th>
              <th style={{ ...th, borderRight: 'none' }}></th>
            </tr>
          </thead>
          <tbody>
            {groups.map(g => {
              const first = g.rows[0];
              return (
                <Fragment key={g.key}>
                  <tr style={{ background: 'var(--bg2)' }}>
                    <td colSpan={11} style={{ padding: '6px 10px', borderBottom: '0.5px solid var(--border)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {first.clientDbId ? (
                          <span style={{ fontWeight: 700, fontSize: 12.5 }}>{first.clientName}</span>
                        ) : (
                          <input value={first.clientName} onChange={renameHypothetical(g.key)} style={{ ...inputSm, width: 170, textAlign: 'left', fontWeight: 700 }} />
                        )}
                        {first.clientCode && <span style={{ fontFamily: 'var(--mono, monospace)', fontSize: 10.5, color: 'var(--text3)' }}>{first.clientCode}</span>}
                        <span onClick={() => removeClientGroup(g.key)} title="Remove client"
                          style={{ marginLeft: 'auto', cursor: 'pointer', color: 'var(--text3)', fontSize: 10.5 }}>✕ remove client</span>
                      </div>
                    </td>
                  </tr>
                  {g.rows.map(r => {
                    const c = computed.find(x => x.uid === r.uid);
                    return (
                      <tr key={r.uid}>
                        <td style={{ ...td, textAlign: 'left' }}></td>
                        <td style={{ ...td, textAlign: 'left' }}>{tag(r.svc)}</td>
                        <td style={{ ...td, textAlign: 'left' }}>
                          <select style={selectSm} value={r.caregiverId} onChange={setCaregiver(r.uid)}>
                            <option value="">— Avg —</option>
                            {caregivers.map(cg => (
                              <option key={cg.id} value={cg.id}>{cg.last_name ? `${cg.last_name}, ${cg.first_name}` : cg.full_name}{cg.position_code ? ` — ${cg.position_code}${cg.is_lead ? ' (Lead)' : ''}` : ''}</option>
                            ))}
                          </select>
                        </td>
                        <td style={td}>
                          <input type="number" step="0.5" style={inputSm} value={r.weeklyHrs} onChange={setCellNumber(r.uid, 'weeklyHrs')} />
                        </td>
                        <td style={td}>
                          <input type="number" step="0.5" style={inputSm} value={r.pay} onChange={setCellNumber(r.uid, 'pay')} />
                        </td>
                        <td style={td}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            {r.billOverride && <span title="Overrides the service-type default" style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--copper)', display: 'inline-block' }} />}
                            <input type="number" step="0.01" style={inputSm} value={r.bill} onChange={setBill(r.uid)} />
                            {r.billOverride && <span onClick={() => resetBill(r.uid)} title="Reset to default" style={{ cursor: 'pointer', fontSize: 10.5, color: 'var(--text3)' }}>↺</span>}
                          </span>
                        </td>
                        <td style={{ ...td, fontVariantNumeric: 'tabular-nums', color: 'var(--text2)' }}>{fmtH(c.hrsMo)}</td>
                        <td style={{ ...td, fontVariantNumeric: 'tabular-nums', color: 'var(--text2)' }}>{fmt$(c.income)}</td>
                        <td style={{ ...td, fontVariantNumeric: 'tabular-nums', color: 'var(--text2)' }}>{fmt$(c.cost)}</td>
                        <td style={{ ...td, borderRight: 'none', fontWeight: 700, color: c.profit >= 0 ? 'var(--success)' : 'var(--danger)' }}>{fmt$(c.profit)}</td>
                        <td style={{ ...td, borderRight: 'none', textAlign: 'center' }}>
                          <span onClick={() => removeRow(r.uid)} title="Remove row" style={{ cursor: 'pointer', color: 'var(--text3)', fontSize: 11 }}>✕</span>
                        </td>
                      </tr>
                    );
                  })}
                </Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ fontWeight: 700, background: 'var(--bg2)' }}>
              <td colSpan={6} style={{ padding: 10, textAlign: 'left', borderTop: '2px solid var(--border2)' }}>TOTAL — {groups.length} clients, {rows.length} service lines</td>
              <td style={{ ...td, padding: 10, borderTop: '2px solid var(--border2)', borderBottom: 'none' }}>{fmtH(totals.hrsMo)}</td>
              <td style={{ ...td, padding: 10, borderTop: '2px solid var(--border2)', borderBottom: 'none' }}>{fmt$(totals.income)}</td>
              <td style={{ ...td, padding: 10, borderTop: '2px solid var(--border2)', borderBottom: 'none' }}>{fmt$(totals.cost)}</td>
              <td style={{ ...td, padding: 10, borderTop: '2px solid var(--border2)', borderBottom: 'none', borderRight: 'none' }}>{fmt$(totals.profit)}</td>
              <td style={{ borderTop: '2px solid var(--border2)' }}></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', border: '0.5px solid var(--border2)', borderTop: 'none', borderRadius: '0 0 8px 8px', flexWrap: 'wrap', gap: 8 }}>
        <span style={{ fontSize: 10.5, color: 'var(--text3)' }}>Every client gets a Skilled and an Unskilled line — leave Hrs/Week at 0 for the service they don't receive.</span>
        <div style={{ display: 'flex', gap: 8, position: 'relative' }}>
          <div style={{ position: 'relative' }}>
            <input className="form-input" style={{ width: 210, padding: '6px 10px', fontSize: 12 }}
              placeholder="Add client from roster…" value={clientQuery}
              onFocus={() => setClientMenuOpen(true)}
              onChange={e => { setClientQuery(e.target.value); setClientMenuOpen(true); }}
              onBlur={() => setTimeout(() => setClientMenuOpen(false), 150)} />
            {clientMenuOpen && (
              <div style={{ position: 'absolute', bottom: 'calc(100% + 6px)', left: 0, width: 300, maxHeight: 260, overflowY: 'auto', background: 'var(--card-bg)', border: '0.5px solid var(--border2)', borderRadius: 10, boxShadow: 'var(--shadow-card)', zIndex: 20 }}>
                {rosterMatches.length === 0 ? (
                  <div style={{ padding: 14, fontSize: 12, color: 'var(--text3)', textAlign: 'center' }}>No matching clients left to add</div>
                ) : rosterMatches.map(c => (
                  <div key={c.id} onMouseDown={() => addClientFromRoster(c)}
                    style={{ padding: '9px 12px', cursor: 'pointer', borderBottom: '0.5px solid var(--border)', display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 12.5 }}>{c.last_name}, {c.first_name}</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)' }}>
                        {c.pa_skilled_hours_week > 0 && `Skilled ${c.pa_skilled_hours_week}h/wk`}
                        {c.pa_skilled_hours_week > 0 && c.pa_pss_hours_week > 0 && ' · '}
                        {c.pa_pss_hours_week > 0 && `Unskilled ${c.pa_pss_hours_week}h/wk`}
                        {!(c.pa_skilled_hours_week > 0) && !(c.pa_pss_hours_week > 0) && 'No active PA hours — both lines start at 0'}
                      </div>
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono, monospace)' }}>{c.client_id}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={addHypotheticalRow}>+ Add hypothetical client</button>
        </div>
      </div>

      <p style={{ marginTop: 12, fontSize: 11, color: 'var(--text3)', lineHeight: 1.6 }}>
        <b style={{ color: 'var(--text2)' }}>Sources when pre-filled:</b> Hrs/Week = clients.pa_skilled_hours_week / pa_pss_hours_week (0 if the client has no
        PA hours for that service). Hrs/Mo = Hrs/Week × weeks in the selected period. Pay Rate defaults to the average active-caregiver rate for that service
        type; picking a name from "Assigned Caregiver" swaps in that caregiver's exact rate. Bill Rate is fixed per service type from the toolbar until you
        override a row. Every field stays editable either way.
      </p>
    </div>
  );
}
